import { normalizePhoneForStorage } from './chatIdentity';

const normalizeText = (value = '') => String(value || '').toLowerCase();

const compactText = (...values) => values
    .filter(Boolean)
    .map((value) => String(value))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

const AI_REPORT_DOCUMENT_KINDS = new Set([
    'ai_report',
    'diagnostic_report',
    'diagnostic_summary',
    'mechanic_report',
    'diy_guide',
    'vehicle_report',
    'history_report',
    'service_history',
    'work_plan',
]);

const AI_REPORT_NAME_PATTERN = /(?:ai_report|vehicle_report|history_report|service_history|work_plan)|\b(?:ai|dash|vehicle|history|service|work)[ _-]*(?:report|history|plan)s?\b/i;

export const DOCUMENT_CATEGORY_DEFINITIONS = [
    { id: 'all', label: 'All', shortLabel: 'All', icon: 'folder' },
    { id: 'ai_report', label: 'Symptom Report', shortLabel: 'Symptom Report', icon: 'spark' },
    { id: 'registration', label: 'Registration', shortLabel: 'Registration', icon: 'card' },
    { id: 'insurance', label: 'Insurance', shortLabel: 'Insurance', icon: 'shield' },
    { id: 'manual', label: 'Manuals', shortLabel: 'Manuals', icon: 'book' },
    { id: 'service_record', label: 'Service Records', shortLabel: 'Service', icon: 'wrench' },
    { id: 'invoice', label: 'Invoices & Receipts', shortLabel: 'Receipts', icon: 'receipt' },
    { id: 'estimate', label: 'Estimates', shortLabel: 'Estimates', icon: 'clipboard' },
    { id: 'obd_report', label: 'OBD & Scan Reports', shortLabel: 'OBD', icon: 'pulse' },
    { id: 'other', label: 'Other Files', shortLabel: 'Other', icon: 'file' },
];

export const DOCUMENT_CATEGORY_BY_ID = DOCUMENT_CATEGORY_DEFINITIONS.reduce((lookup, category) => {
    lookup[category.id] = category;
    return lookup;
}, {});

export function getDocumentVehicleId(doc) {
    return doc?.vehicle_id || doc?.vehicleId || doc?.vehicle?.vehicle_id || '';
}

export function getDocumentTimestamp(doc) {
    const raw = doc?.updated_at || doc?.updatedAt || doc?.created_at || doc?.createdAt || doc?.uploaded_at || doc?.uploadedAt;
    const time = raw ? new Date(raw).getTime() : NaN;
    return Number.isFinite(time) ? time : 0;
}

export function getLatestDocumentForCategory(enrichedDocuments = [], categoryId = '') {
    return (enrichedDocuments || [])
        .filter((item) => item?.category?.id === categoryId)
        .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))[0] || null;
}

export function buildPublicManualLinks(vehicle = {}) {
    const vehicleLabel = [vehicle?.year, vehicle?.make, vehicle?.model].filter(Boolean).join(' ').trim();
    if (!vehicleLabel) return [];
    const query = encodeURIComponent(`${vehicleLabel} owner manual OEM PDF`);
    return [{
        kind: 'manual_link',
        title: `${vehicleLabel} OEM manual search`,
        url: `https://www.google.com/search?q=${query}`,
        source: 'public_search',
    }];
}

export function getDocumentDateLabel(doc) {
    const timestamp = getDocumentTimestamp(doc);
    if (!timestamp) return 'Unknown date';
    return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    }).format(new Date(timestamp));
}

export function inferDocumentCategory(doc = {}) {
    const kind = normalizeText(doc.document_kind || doc.documentKind || doc.media_kind || doc.mediaKind);
    const name = normalizeText(compactText(doc.name, doc.original_filename, doc.file_type, doc.path, doc.storage_url, doc.storageUrl));
    const text = `${kind} ${name}`;

    if (isAgentGeneratedReport(doc)) return DOCUMENT_CATEGORY_BY_ID.ai_report;
    if (/registration|title|dmv|tag|plate/.test(text)) return DOCUMENT_CATEGORY_BY_ID.registration;
    if (/insurance|policy|coverage/.test(text)) return DOCUMENT_CATEGORY_BY_ID.insurance;
    if (/manual|owner.?s manual|handbook/.test(text)) return DOCUMENT_CATEGORY_BY_ID.manual;
    if (/invoice|receipt|paid|payment/.test(text)) return DOCUMENT_CATEGORY_BY_ID.invoice;
    if (/estimate|quote/.test(text)) return DOCUMENT_CATEGORY_BY_ID.estimate;
    if (/obd|scan|code|dtc|diagnostic/.test(text)) return DOCUMENT_CATEGORY_BY_ID.obd_report;
    if (/service|repair|maintenance|work order|history|log/.test(text)) return DOCUMENT_CATEGORY_BY_ID.service_record;
    return DOCUMENT_CATEGORY_BY_ID.other;
}

export function isAgentGeneratedReport(doc = {}) {
    const source = normalizeText(doc.source);
    if (source !== 'agent_generated') return false;

    const kind = normalizeText(doc.document_kind || doc.documentKind);
    if (AI_REPORT_DOCUMENT_KINDS.has(kind)) return true;

    const text = compactText(
        doc.name,
        doc.original_filename,
        doc.originalFilename,
        doc.path,
        doc.storage_url,
        doc.storageUrl
    );
    return AI_REPORT_NAME_PATTERN.test(text);
}

function parseMaybeObject(value) {
    if (!value) return null;
    if (typeof value === 'object') return value;
    if (typeof value !== 'string') return null;
    try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
        return null;
    }
}

function findExpiryDate(doc = {}) {
    const markers = parseMaybeObject(doc.extracted_markers || doc.extractedMarkers);
    const candidates = [
        doc.expires_at,
        doc.expiresAt,
        doc.expiry_date,
        doc.expiryDate,
        doc.expiration_date,
        doc.expirationDate,
        markers?.expires_at,
        markers?.expiry_date,
        markers?.expiration_date,
        markers?.policy_expiration,
        markers?.registration_expiration,
    ];

    for (const candidate of candidates) {
        const date = candidate ? new Date(candidate) : null;
        if (date && Number.isFinite(date.getTime())) return date;
    }
    return null;
}

export function getDocumentStatus(doc) {
    const expiry = findExpiryDate(doc);
    if (!expiry) {
        return {
            id: 'current',
            label: 'On file',
            classes: 'bg-[#F9F9F9] text-[#414141]',
            sortRank: 2,
        };
    }

    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / 86400000);
    if (daysUntilExpiry < 0) {
        return {
            id: 'expired',
            label: 'Expired',
            classes: 'bg-rose-50 text-rose-600',
            sortRank: 0,
        };
    }
    if (daysUntilExpiry <= 45) {
        return {
            id: 'expiring',
            label: 'Expiring soon',
            classes: 'bg-amber-50 text-amber-700',
            sortRank: 1,
        };
    }
    return {
        id: 'current',
        label: 'Current',
        classes: 'bg-emerald-50 text-emerald-700',
        sortRank: 3,
    };
}

export function getDocumentSearchText(doc = {}) {
    return compactText(
        doc.name,
        doc.original_filename,
        doc.file_type,
        doc.source,
        doc.document_kind,
        doc.media_kind,
        doc.extracted_text,
        doc.vision_summary,
        inferDocumentCategory(doc)?.label
    ).toLowerCase();
}

const normalizeVehicleLabel = (value = '') => String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const isTicketClosed = (ticket) => {
    const status = String(ticket?.status || ticket?.ticketStatus || '').toLowerCase();
    return status === 'closed' || status === 'resolved' || status === 'abandoned';
};

// vehicleLabel is passed in (rather than derived here) since callers already
// compute it locally and the derivation itself isn't worth sharing.
export const ticketBelongsToVehicle = (ticket, vehicle, vehicleLabel) => {
    if (!ticket || !vehicle) return false;
    const ticketVehicleId = ticket.vehicle_id || ticket.vehicleId || ticket.vehicle?.vehicle_id;
    if (ticketVehicleId && vehicle.vehicle_id) {
        return String(ticketVehicleId) === String(vehicle.vehicle_id);
    }

    // Fall back to label matching when the ticket has no vehicle id.
    const ticketLabel = normalizeVehicleLabel(
        ticket.vehicle_label || ticket.vehicleLabel ||
        [ticket.vehicle?.year, ticket.vehicle?.make, ticket.vehicle?.model].filter(Boolean).join(' ')
    );
    const normalizedVehicleLabel = normalizeVehicleLabel(vehicleLabel || '');
    return Boolean(ticketLabel && normalizedVehicleLabel && (
        ticketLabel === normalizedVehicleLabel ||
        ticketLabel.includes(normalizedVehicleLabel) ||
        normalizedVehicleLabel.includes(ticketLabel)
    ));
};

export const ticketRealId = (ticket) => (
    ticket?.diagnostic_ticket_id || ticket?.ticket_id || ticket?.id || ticket?.ticket_number || null
);

export const ticketThreadId = (ticket) => (
    ticket?.thread_id || ticket?.source_repair_log_id || ticket?.repair_log_id || null
);

export const ticketVehicleLabel = (ticket) => {
    const v = ticket?.vehicle || {};
    const fromVehicle = [v.year, v.make, v.model].filter(Boolean).join(' ');
    return fromVehicle || ticket?.vehicle_label || ticket?.vehicleLabel || 'Your vehicle';
};

export const ticketTitle = (ticket) => (
    ticket?._displayTitle || ticket?.title || ticket?.symptom_summary || ticket?.summary || 'Diagnostic issue'
);

// A title is "generic" when it came from the bare "Start Diagnostic" seed rather
// than the user's actual symptom, so we should fall back to a better source.
const isGenericTicketName = (value) => {
    const t = String(value || '').toLowerCase().trim();
    if (!t) return true;
    return t.includes('start diagnostic') ||
        t === 'diagnosis' ||
        t === 'new diagnostic' ||
        t === 'vehicle diagnostic issue';
};

const cleanDiagnosisSuffix = (value) => String(value || '').replace(/\s+diagnosis$/i, '').trim();

// Resolve the name to display for a ticket, preferring the thread title shown in
// the sidebar (the "original name"), then a non-generic ticket title/symptom.
export const resolveTicketDisplayTitle = (ticket, threadTitleById) => {
    const threadTitle = threadTitleById?.get(String(ticketThreadId(ticket))) || '';
    if (!isGenericTicketName(threadTitle)) return cleanDiagnosisSuffix(threadTitle);
    const title = String(ticket?.title || '').trim();
    if (!isGenericTicketName(title)) return cleanDiagnosisSuffix(title);
    const symptom = String(ticket?.symptom_summary || '').trim();
    if (!isGenericTicketName(symptom)) return symptom;
    return cleanDiagnosisSuffix(title) || symptom || 'Diagnostic issue';
};

export const ticketPathLabel = (path) => {
    const p = String(path || '').toLowerCase();
    if (p === 'diy') return 'DIY';
    if (p === 'shop') return 'Shop';
    if (p === 'monitoring') return 'Monitoring';
    return null;
};

export const formatTimeAgo = (value) => {
    if (!value) return '';
    const then = new Date(value).getTime();
    if (Number.isNaN(then)) return '';
    const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (secs < 60) return 'just now';
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(months / 12)}y ago`;
};

export const CONVERSATION_KIND_DEFINITIONS = [
    { id: 'all', label: 'All' },
    { id: 'diagnosis', label: 'Diagnosis' },
    { id: 'parts', label: 'Parts' },
    { id: 'maintenance', label: 'Maintenance' },
    { id: 'service', label: 'Service' },
    { id: 'modification', label: 'Mods' },
    { id: 'shop', label: 'Shop' },
    { id: 'general', label: 'General' },
];

export function inferConversationKind({ title = '', searchText = '', ticket = null } = {}) {
    const text = normalizeText(compactText(title, searchText, ticket?.title, ticket?.symptom_summary, ticket?.summary));
    if (ticket) return 'diagnosis';
    if (/\b(part|parts|tool|tools|fitment|oem|aftermarket|replacement|replace)\b/.test(text)) return 'parts';
    if (/\b(mod|mods|modification|upgrade|lift kit|tune|exhaust|suspension|wheels|tires)\b/.test(text)) return 'modification';
    if (/\b(service log|log service|repair history|service history|receipt|invoice|completed service)\b/.test(text)) return 'service';
    if (/\b(maintenance|oil change|tire rotation|coolant flush|brake fluid|interval|due|scheduled service)\b/.test(text)) return 'maintenance';
    if (/\b(diagnos|symptom|warning light|check engine|obd|dtc|fault code|no start|overheat|misfire|noise|leak)\b/.test(text)) return 'diagnosis';
    return 'general';
}

export function readLocalStringSet(key) {
    if (typeof window === 'undefined' || !key) return new Set();
    try {
        const parsed = JSON.parse(window.localStorage.getItem(key) || '[]');
        return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
    } catch {
        return new Set();
    }
}

export function writeLocalStringSet(key, setValue) {
    if (typeof window === 'undefined' || !key) return;
    try {
        window.localStorage.setItem(key, JSON.stringify(Array.from(setValue || []).map(String)));
    } catch {}
}

export function getPinnedConversationStorageKey(phone) {
    return `dash_pinned_conversations:${normalizePhoneForStorage(phone)}`;
}

export function getDismissedGarageWorkStorageKey(phone) {
    return `dash_garage_dismissed_work:${normalizePhoneForStorage(phone)}`;
}
